import techBg from "@/assets/tech-bg.png.asset.json";
import { useTranslation } from "react-i18next";


type IconProps = { className?: string };

const IconVision = ({ className }: IconProps) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="8" width="26" height="17" />
    <circle cx="16" cy="16.5" r="5" />
    <rect x="11" y="4" width="10" height="4" />
  </svg>
);
const IconWeight = ({ className }: IconProps) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="6" width="26" height="5" />
    <rect x="3" y="20" width="26" height="5" />
    <path d="M8 11v9M24 11v9" />
  </svg>
);
const IconEdge = ({ className }: IconProps) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="9" y="9" width="14" height="14" />
    <path d="M13 3v6M19 3v6M13 23v6M19 23v6M3 13h6M3 19h6M23 13h6M23 19h6" />
  </svg>
);
const IconCode = ({ className }: IconProps) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M11 10l-7 6 7 6M21 10l7 6-7 6M18 7l-4 18" />
  </svg>
);
const IconApi = ({ className }: IconProps) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="6" cy="16" r="3" />
    <circle cx="26" cy="8" r="3" />
    <circle cx="26" cy="24" r="3" />
    <path d="M9 15l14-6M9 17l14 6" />
  </svg>
);
const IconAi = ({ className }: IconProps) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="16" cy="16" r="11" />
    <path d="M16 5v22M5 16h22M9 9l14 14M23 9L9 23" />
  </svg>
);

const TechnologySection = () => {
  const { t } = useTranslation();

  const capabilities = [
    {
      Icon: IconVision,
      title: t("sHome.technologySection.cap1Title"),
      text: t("sHome.technologySection.cap1Text"),
    },
    {
      Icon: IconWeight,
      title: t("sHome.technologySection.cap2Title"),
      text: t("sHome.technologySection.cap2Text"),
    },
    {
      Icon: IconEdge,
      title: t("sHome.technologySection.cap3Title"),
      text: t("sHome.technologySection.cap3Text"),
    },
    {
      Icon: IconCode,
      title: t("sHome.technologySection.cap4Title"),
      text: t("sHome.technologySection.cap4Text"),
    },
    {
      Icon: IconApi,
      title: t("sHome.technologySection.cap5Title"),
      text: t("sHome.technologySection.cap5Text"),
    },
    {
      Icon: IconAi,
      title: t("sHome.technologySection.cap6Title"),
      text: t("sHome.technologySection.cap6Text"),
    },
  ];

  const stats = [
    { value: t("sHome.technologySection.stat1Value"), label: t("sHome.technologySection.stat1Label") },
    { value: t("sHome.technologySection.stat2Value"), label: t("sHome.technologySection.stat2Label") },
    { value: t("sHome.technologySection.stat3Value"), label: t("sHome.technologySection.stat3Label") },
    { value: t("sHome.technologySection.stat4Value"), label: t("sHome.technologySection.stat4Label") },
  ];

  return (

    <section id="tecnologia" data-header-theme="blue" className="relative bg-black text-black">
      <img
        src={techBg.url}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="relative container mx-auto px-4 md:px-6 py-14 md:py-28">
        <div className="border border-black/15 bg-brand-blue">
          {/* Top: intro + impact card */}
          <div className="grid lg:grid-cols-2">
            <div className="p-6 sm:p-8 md:p-12">
              <h2
                className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal leading-[1.12] tracking-[-0.01em]"
              >
                <span className="text-black/45">{t("sHome.technologySection.headlineMuted")}</span>{" "}
                <span className="text-black">
                  {t("sHome.technologySection.headlineBody")}
                </span>

              </h2>

            </div>

            <div className="grid grid-cols-2 gap-3 md:gap-4 border-t border-black/15 bg-black/15 lg:border-t-0 lg:border-l">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className="flex min-h-[9rem] sm:min-h-[12rem] md:min-h-[15rem] flex-col justify-between bg-brand-ink p-5 sm:p-7 md:p-12 text-white"
                >
                  <span className="font-dot text-4xl sm:text-6xl md:text-8xl leading-none tracking-[0.08em] text-brand-blue">
                    {s.value}
                  </span>
                  <span className="mt-6 text-sm sm:text-base md:text-xl text-white">{s.label}</span>

                </div>
              ))}
            </div>


          </div>

          {/* Bottom: capabilities */}
          <div className="grid border-t border-black/15 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((c, i) => (
              <div
                key={c.title}
                className="group border-black/15 p-6 sm:p-8 md:p-10 border-b last:border-b-0 sm:last:border-b sm:[&:nth-child(odd)]:border-r lg:[&:nth-child(odd)]:border-r-0 lg:[&:not(:nth-child(3n))]:border-r sm:[&:nth-child(-n+4)]:border-b lg:[&:nth-child(-n+3)]:border-b"
              >
                <c.Icon className="h-8 w-8 text-black transition-transform duration-500 group-hover:scale-110" />
                <h3 className="mt-6 text-base md:text-lg">{c.title}</h3>
                <p className="mt-3 text-base md:text-lg text-black/55 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default TechnologySection;
